/**
 * DeskWorks Remote contracts shared by the API, the web/desktop/mobile viewers and (by
 * specification) the Rust host agent. The wire protocol is documented in docs/remote/PROTOCOL.md.
 */
import { z } from 'zod';

export const REMOTE_PERMISSIONS = ['view', 'control', 'clipboard', 'files'] as const;
export type RemotePermission = (typeof REMOTE_PERMISSIONS)[number];
export type RemotePermissions = Record<RemotePermission, boolean>;

export const NO_REMOTE_PERMISSIONS: RemotePermissions = { view: false, control: false, clipboard: false, files: false };

export type RemoteOs = 'windows' | 'macos' | 'linux';
export type RemoteHostKind = 'support' | 'managed';
export type RemoteHostScope = 'personal' | 'workspace';
export type RemoteAccessRole = 'owner' | 'admin' | 'control' | 'view';
export type RemoteSessionKind = 'attended' | 'unattended';
export type RemoteSessionState = 'awaiting_consent' | 'connecting' | 'active' | 'ended' | 'declined' | 'expired' | 'failed';
export type RemoteQuality = 'speed' | 'balanced' | 'sharp';

/** States in which a session still holds resources and can change. */
export const LIVE_REMOTE_STATES: readonly RemoteSessionState[] = ['awaiting_consent', 'connecting', 'active'];

/** Signed envelope (PROTOCOL.md §5.1). All fields are base64url without padding. */
export interface SignedEnvelope {
  p: string;
  k: string;
  s: string;
}

export const SignedEnvelopeSchema = z.object({
  p: z.string().min(1).max(8192).regex(/^[A-Za-z0-9_-]+$/),
  k: z.string().length(43).regex(/^[A-Za-z0-9_-]+$/),
  s: z.string().length(86).regex(/^[A-Za-z0-9_-]+$/),
});

export type EnvelopeKind = 'authority' | 'cert' | 'grant' | 'enroll' | 'answer';

export interface AuthorityPayload {
  v: 1;
  ws: string;
  ver: number;
  keys: string[];
  iat: number;
  /** Owner reset after every key was lost (PROTOCOL.md §5.3). */
  reset?: boolean;
  /**
   * Strict mode: computers that pin this set accept unattended sessions only with a grant signed
   * by these keys. Absent (the default, Standard mode): a signed-in person with access is enough.
   */
  strict?: boolean;
}

export interface CertPayload {
  v: 1;
  ws: string;
  sub: string;
  uid: string;
  name: string;
  hosts: '*' | string[];
  perms: (RemotePermission | 'unattended')[];
  iat: number;
  exp: number;
}

export interface GrantPayload {
  v: 1;
  sid: string;
  hid: string;
  uid: string;
  perms: RemotePermission[];
  fp: string;
  iat: number;
  exp: number;
}

export interface EnrollPayload {
  v: 1;
  ws: string;
  hid: string;
  hpk: string;
  scope: RemoteHostScope;
  unattended: boolean;
  iat: number;
}

export interface AnswerPayload {
  v: 1;
  sid: string;
  fp: string;
  iat: number;
}

export const DeviceDescriptorSchema = z.object({
  name: z.string().trim().min(1).max(120),
  os: z.enum(['windows', 'macos', 'linux']),
  osVersion: z.string().trim().max(60).default(''),
  arch: z.string().trim().max(20).default(''),
  agentVersion: z.string().trim().max(40).default(''),
  publicKey: z.string().length(43).regex(/^[A-Za-z0-9_-]+$/),
});
export type DeviceDescriptor = z.infer<typeof DeviceDescriptorSchema>;

export interface RemotePerson {
  id: string;
  name: string;
  initials: string;
  email: string;
}

export interface RemoteHost {
  id: string;
  kind: RemoteHostKind;
  name: string;
  os: RemoteOs;
  osVersion: string;
  agentVersion: string;
  workspaceId: string | null;
  workspaceName: string | null;
  scope: RemoteHostScope;
  ownerId: string | null;
  ownerName: string | null;
  online: boolean;
  lastSeenAt: string | null;
  enrolledAt: string;
  enrolledByName: string | null;
  allowUnattended: boolean;
  myRole: RemoteAccessRole;
  publicKey: string;
  lastSession: { at: string; technicianName: string } | null;
}

export interface RemoteHostAccess {
  userId: string;
  name: string;
  email: string;
  role: 'control' | 'view';
  grantedAt: string;
}

export interface RemoteSession {
  id: string;
  kind: RemoteSessionKind;
  state: RemoteSessionState;
  hostId: string;
  hostName: string;
  hostOs: RemoteOs;
  workspaceId: string | null;
  technician: RemotePerson;
  conversationId: string | null;
  permissions: RemotePermissions;
  path: 'direct' | 'relayed' | null;
  requestedAt: string;
  consentedAt: string | null;
  connectedAt: string | null;
  endedAt: string | null;
  endReason: string | null;
}

export interface RemoteSupportCode {
  id: string;
  code: string;
  display: string;
  workspaceId: string;
  expiresAt: string;
  state: 'waiting' | 'redeemed' | 'expired' | 'cancelled';
  sessionId: string | null;
}

export interface RemoteEnrollment {
  id: string;
  workspaceId: string;
  code: string | null;
  scope: RemoteHostScope;
  allowUnattended: boolean;
  maxUses: number;
  uses: number;
  expiresAt: string;
  createdAt: string;
  authorityFingerprint: string | null;
}

export interface RemoteIceServer {
  urls: string[];
  username?: string;
  credential?: string;
}

export interface RemoteTicket {
  ticket: string;
  iceServers: RemoteIceServer[];
  hostPublicKey: string;
  session: RemoteSession;
}

export interface RemoteUserKey {
  id: string;
  userId: string;
  userName: string;
  publicKey: string;
  fingerprint: string;
  label: string;
  createdAt: string;
  revokedAt: string | null;
  /** Workspaces where this key is an authority or holds a certificate. */
  standing: { workspaceId: string; authority: boolean; cert: SignedEnvelope | null }[];
}

export interface RemoteAuthority {
  workspaceId: string;
  version: number;
  /** Strict mode is on (unattended access needs an approved browser or phone). */
  strict: boolean;
  envelope: SignedEnvelope | null;
  keys: { publicKey: string; fingerprint: string; userName: string | null; label: string | null }[];
}

export interface WorkspaceDomain {
  domain: string;
  /** The TXT record to publish (owners and admins only). */
  record: string | null;
  verifiedAt: string | null;
  createdAt: string;
}

export interface RemoteLinkPreview {
  userCode: string;
  /** Reserved host id and the computer's device key, for signing the `enroll` approval. */
  hostId: string;
  publicKey: string;
  device: { name: string; os: RemoteOs; osVersion: string; agentVersion: string; fingerprint: string };
  expiresAt: string;
  workspaces: { id: string; name: string; role: string; canManage: boolean }[];
}

/* ------------------------------------------------------------------ requests */

const uuid = z.uuid();

export const CreateSupportCodeSchema = z.object({
  workspaceId: uuid,
  conversationId: uuid.nullable().optional(),
});

export const CreateEnrollmentSchema = z.object({
  workspaceId: uuid,
  scope: z.enum(['personal', 'workspace']).default('workspace'),
  allowUnattended: z.boolean().default(true),
  maxUses: z.number().int().min(1).max(500).default(1),
  expiresInHours: z.number().int().min(1).max(720).default(24),
});

export const ApproveLinkSchema = z.object({
  workspaceId: uuid,
  scope: z.enum(['personal', 'workspace']).default('personal'),
  allowUnattended: z.boolean().default(true),
  approval: SignedEnvelopeSchema.nullable().optional(),
});

export const StartRemoteSessionSchema = z.object({
  permissions: z.array(z.enum(REMOTE_PERMISSIONS)).min(1).max(4).default(['view', 'control']),
  grant: SignedEnvelopeSchema.nullable().optional(),
  cert: SignedEnvelopeSchema.nullable().optional(),
  conversationId: uuid.nullable().optional(),
});

export const UpdateRemoteHostSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  allowUnattended: z.boolean().optional(),
});

export const RemoteAccessSchema = z.object({ role: z.enum(['control', 'view']) });

export const RegisterRemoteKeySchema = z.object({
  publicKey: z.string().length(43).regex(/^[A-Za-z0-9_-]+$/),
  label: z.string().trim().min(1).max(120),
});

/* ------------------------------------------------------------------- helpers */

/** "482913557" → "482 913 557". */
export function formatSupportCode(code: string): string {
  const digits = code.replace(/\D/g, '');
  return digits.replace(/(\d{3})(?=\d)/g, '$1 ');
}

/** Normalizes what a person typed ("482-913 557") to digits only. */
export function normalizeSupportCode(input: string): string {
  return input.replace(/\D/g, '');
}

/** Alphabet for enrollment and link codes: no 0/O, 1/I/L or U, so codes read aloud cleanly. */
export const ENROLL_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ23456789';

/** Enrollment and link codes are typed in groups of four; case and separators don't matter. */
export function normalizeEnrollCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function formatEnrollCode(code: string): string {
  return normalizeEnrollCode(code).replace(/(.{4})(?=.)/g, '$1-');
}

export function permissionsFromList(list: readonly RemotePermission[]): RemotePermissions {
  const out = { ...NO_REMOTE_PERMISSIONS };
  for (const p of list) out[p] = true;
  return out;
}

export function remoteOsLabel(os: RemoteOs): string {
  return os === 'windows' ? 'Windows' : os === 'macos' ? 'macOS' : 'Linux';
}

export function remoteStateLabel(state: RemoteSessionState): string {
  switch (state) {
    case 'awaiting_consent':
      return 'Waiting for permission';
    case 'connecting':
      return 'Connecting';
    case 'active':
      return 'Connected';
    case 'ended':
      return 'Ended';
    case 'declined':
      return 'Declined';
    case 'expired':
      return 'Expired';
    case 'failed':
      return 'Could not connect';
  }
}
